import React, { useState } from 'react';
import { PlayCircle, PauseCircle, XCircle, Star, CheckCircle, Clock, ImageOff, Calendar, Bookmark } from 'lucide-react';

const AnimeCard = ({ anime, onClick }) => {
  const displayTitle = anime.english || anime.romaji || anime.french || anime.japanese || anime.voiranime_slug;
  const userRating = anime.rating;
  const startWatch = anime.start_watch_date;
  const endWatch = anime.end_watch_date;
  const watchedCount = anime.episodes_watched || 0;

  // On calcule la date la plus récente parmi toutes les sources disponibles
  const getBestWatchDate = () => {
    const dates = [
      anime.last_watched_date,
      anime.end_watch_date,
      anime.mal_start_date,
      anime.mal_finish_date
    ].map(d => d ? new Date(d).getTime() : 0);

    const maxTime = Math.max(...dates);
    return maxTime > 0 ? new Date(maxTime) : null;
  };

  const displayDate = getBestWatchDate();
  const totalEps = anime.total_episodes > 0 ? anime.total_episodes : (anime.latest_voiranime_ep > 0 ? anime.latest_voiranime_ep : 0);
  const isEstimatedEps = !anime.total_episodes && anime.latest_voiranime_ep > 0;

  const [imgError, setImgError] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "Jamais";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const currentProgress = anime.end_watch_num || watchedCount || 0;
  const isPlanToWatch = anime.mal_status === 'plan_to_watch';
  const isOnHold = anime.mal_status === 'on_hold';
  const isDropped = anime.mal_status === 'dropped';

  // Si on a le nombre total d'épisodes, on calcule le pourcentage.
  // Sinon (total_episodes inconnu/0), on bloque la barre à 60%.
  const isUnknownTotal = totalEps <= 0;
  const progressPercentage = isUnknownTotal
    ? 60
    : Math.min((currentProgress / totalEps) * 100, 100);

  return (
    <div
      className="relative aspect-[2/3] rounded-xl overflow-hidden shadow-lg group bg-gray-900 cursor-pointer"
      onClick={onClick}
    >
      <div className="absolute inset-0 z-0">
        {anime.cover_image_url && !imgError ? (
          <img
            src={anime.cover_image_url}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 bg-gray-800">
            <ImageOff size={48} />
          </div>
        )}
      </div>

      <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20 z-10 pointer-events-none"></div>

      {progressPercentage === 100 && (
        <div className="absolute top-2 right-2 z-30 bg-black/60 backdrop-blur-md rounded-full p-1.5 text-green-400 shadow-sm">
          <CheckCircle size={18} />
        </div>
      )}

      {/* --- ZONE CONTENU (Overlay en bas) --- */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-gray-900 via-gray-900/90 to-transparent pt-24 flex flex-col justify-end">

        <h3 className="text-lg font-bold leading-tight mb-1 line-clamp-2 text-white group-hover:text-blue-300 transition-colors text-balance" title={displayTitle}>
          {displayTitle}
        </h3>

        {anime.romaji && anime.romaji !== displayTitle && (
          <p className="text-xs text-gray-300 mb-3 truncate opacity-80">{anime.romaji}</p>
        )}

        <div className="space-y-2 text-sm">
          {isPlanToWatch ? (
            <div className="flex items-center justify-between mt-2 mb-1">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-500/20 text-indigo-300 rounded-md backdrop-blur-sm border border-indigo-500/30">
                <Bookmark size={14} />
                <span className="text-xs font-semibold">À voir</span>
              </div>
              {totalEps > 0 && (
                <span className="text-xs font-medium text-gray-400 bg-gray-800/80 px-2 py-1 rounded-md backdrop-blur-sm border border-gray-700/50">
                  {isEstimatedEps ? '~' : ''}{totalEps} eps
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md backdrop-blur-sm ${isDropped ? 'bg-red-500/10 text-red-300' :
                  isOnHold ? 'bg-orange-500/10 text-orange-300' :
                    'text-gray-300'
                  }`}>
                  {isOnHold ? <PauseCircle size={14} className="text-orange-400" /> :
                    isDropped ? <XCircle size={14} className="text-red-400" /> :
                      <PlayCircle size={14} className="text-blue-400" />}
                  <span className="text-xs">
                    <span className="font-bold text-white">{currentProgress}</span>
                    {totalEps > 0 && <span className="text-gray-400">/{isEstimatedEps ? '~' : ''}{totalEps}</span>}
                  </span>
                </div>

                {userRating !== null && userRating !== undefined && (
                  <div className="flex items-center text-yellow-400 gap-1 px-2 py-1 rounded-md bg-yellow-400/10 backdrop-blur-sm">
                    <Star size={14} fill="currentColor" />
                    <span className="text-xs font-bold">{userRating}</span>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-400 flex items-center gap-1.5 opacity-75">
                <Clock size={12} />
                <span>{displayDate ? formatDate(displayDate) : "Jamais"}</span>
              </div>
            </>
          )}
        </div>

        {!isPlanToWatch && (
          <div className="mt-3">
            <div className="w-full bg-gray-700/50 h-1 rounded-full overflow-hidden backdrop-blur-md">
              <div
                className={`h-full transition-all duration-500 ${progressPercentage === 100 ? 'bg-green-500' :
                  isDropped ? 'bg-red-500' :
                    isOnHold ? 'bg-orange-500' :
                      'bg-blue-500'
                  }`}
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimeCard;